import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Trash2, Eye, User as UserIcon, Mail, Shield, ExternalLink, Ban, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const IMG = (u) => `${process.env.REACT_APP_BACKEND_URL}${u}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
const StatusTag = ({ s }) => (
  <span className={`tag !text-[9px] !px-2 !py-0.5 ${s === "approved" ? "bg-neon text-black" : s === "rejected" ? "bg-danger text-white" : "bg-gold text-black"}`}>
    {s === "approved" ? "Published" : s === "rejected" ? "Removed" : "Pending"}
  </span>
);

// Shared admin-only user detail — account info + their posts/memes. Email is admin-only.
function UserDetailDialog({ userId, onClose }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-user-detail", userId],
    queryFn: async () => (await api.get(`/admin/users/${userId}`)).data,
    enabled: !!userId,
  });
  const suspend = useMutation({
    mutationFn: ({ id, suspended }) => api.patch(`/admin/users/${id}/suspend`, { suspended }),
    onSuccess: (_r, v) => {
      toast.success(v.suspended ? "User suspended" : "User reinstated");
      qc.invalidateQueries({ queryKey: ["admin-user-detail", userId] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const u = data?.user;
  const isAdmin = u?.role === "admin";
  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-ink2 border-3 border-black text-cream max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-user-detail">
        <DialogHeader><DialogTitle className="font-display font-900 uppercase text-neon">User Detail</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Admin-only account details, stats and posts for this user.</DialogDescription>
        {isLoading || !data ? <p className="font-mono text-dim text-sm">Loading…</p> : (
          <div className="space-y-5">
            {u.suspended && (
              <div className="border-3 border-danger bg-danger/10 p-2 flex items-center gap-2" data-testid="admin-user-suspended-banner">
                <Ban className="w-4 h-4 text-danger" /><span className="font-mono text-[11px] text-danger uppercase">Account suspended — sign-in & posting are blocked</span>
              </div>
            )}
            <div className="comic-card !bg-ink p-4 space-y-2">
              <div className="flex items-center gap-2">
                <UserIcon className="w-4 h-4 text-neon" />
                <span className="font-display font-900 text-cream text-lg">{u.displayName}</span>
                {isAdmin && <span className="tag !text-[9px] !px-2 !py-0.5 bg-grape text-cream">admin</span>}
              </div>
              {u.username && <p className="font-mono text-[11px] text-neon">@{u.username}</p>}
              <p className="font-mono text-xs text-cream flex items-center gap-2" data-testid="admin-user-detail-email">
                <Mail className="w-3.5 h-3.5 text-dim" /> {u.email || <span className="text-dim">no email (wallet/anon)</span>}
                {u.email && (u.emailVerified ? <span className="text-neon text-[10px]">verified</span> : <span className="text-gold text-[10px]">unverified</span>)}
              </p>
              {u.bio && <p className="font-mono text-xs text-dim">"{u.bio}"</p>}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1 font-mono text-[10px] text-dim">
                <div>Joined <span className="text-cream">{fmtDate(u.created_at)}</span></div>
                <div>Auth <span className="text-cream">{(u.authProviders || []).join(", ")}</span></div>
                <div>Last active <span className="text-cream">{fmtDate(data.stats.lastActivity)}</span></div>
              </div>
              {/* Ban / reinstate toggle */}
              {isAdmin ? (
                <p className="font-mono text-[10px] text-dim pt-1">Admin accounts can't be suspended.</p>
              ) : u.suspended ? (
                <button onClick={() => suspend.mutate({ id: u.user_id, suspended: false })} disabled={suspend.isPending}
                  className="btn-ghost !py-1.5 !px-3 !text-[11px] !border-neon hover:!bg-neon mt-1" data-testid="admin-user-unsuspend-btn">
                  <ShieldCheck className="w-3.5 h-3.5" /> Reinstate Account
                </button>
              ) : (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="btn-ghost !py-1.5 !px-3 !text-[11px] !border-danger !text-danger hover:!bg-danger hover:!text-white mt-1" data-testid="admin-user-suspend-btn">
                      <Ban className="w-3.5 h-3.5" /> Suspend Account
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-ink2 border-3 border-black text-cream">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-display font-900 uppercase text-danger">Suspend this account?</AlertDialogTitle>
                      <AlertDialogDescription className="font-mono text-xs text-dim">
                        {u.displayName} will be signed out and blocked from logging in or posting until reinstated. Their existing posts stay up.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="border-3 border-black bg-ink3 text-cream font-heading uppercase text-xs" data-testid="admin-user-suspend-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => suspend.mutate({ id: u.user_id, suspended: true })}
                        className="border-3 border-black bg-danger text-white font-heading uppercase text-xs" data-testid="admin-user-suspend-confirm">Suspend</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
              {[["Posts", data.stats.posts], ["Memes", data.stats.memes], ["Published", data.stats.approved],
                ["Pending", data.stats.pending], ["Removed", data.stats.rejected], ["Upvotes", data.stats.upvotes]].map(([l, v]) => (
                <div key={l} className="comic-card !bg-ink p-2">
                  <p className="font-display font-900 text-neon text-lg">{v}</p>
                  <p className="font-mono text-[9px] uppercase text-dim">{l}</p>
                </div>
              ))}
            </div>
            <div>
              <p className="font-display font-900 uppercase text-xs text-cream mb-2">Their Posts & Memes ({data.posts.length})</p>
              <div className="space-y-2">
                {data.posts.length === 0 && <p className="font-mono text-xs text-dim">No posts yet.</p>}
                {data.posts.map((p) => (
                  <div key={p.id} className="comic-card !bg-ink p-3 flex gap-3 items-start" data-testid={`admin-user-post-${p.id}`}>
                    {p.image_url && <img src={IMG(p.image_url)} alt="meme" className="w-16 h-16 object-cover border-3 border-black bg-white shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2"><StatusTag s={p.status} /><span className="font-mono text-[10px] text-dim">{fmtDate(p.created_at)}</span></div>
                      {p.story && <p className="font-mono text-xs text-cream/90 mt-1 line-clamp-2">"{p.story}"</p>}
                      <p className="font-mono text-[10px] text-dim mt-1">▲ {p.upvotes || 0} upvotes{p.isFavorite ? " · ★ fave" : ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Small styled trigger so the AlertDialog opens from a themed button.

export function AdminUsersPanel() {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");
  const [detailUserId, setDetailUserId] = useState(null);
  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get("/admin/users")).data,
  });
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let r = users.filter((u) =>
      !needle ||
      (u.displayName || "").toLowerCase().includes(needle) ||
      (u.username || "").toLowerCase().includes(needle) ||
      (u.email || "").toLowerCase().includes(needle));
    r = [...r].sort((a, b) => {
      if (sort === "active") return (b.postsCount || 0) - (a.postsCount || 0);
      if (sort === "oldest") return (a.created_at || "").localeCompare(b.created_at || "");
      return (b.created_at || "").localeCompare(a.created_at || ""); // newest
    });
    return r;
  }, [users, q, sort]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-dim absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, username or email…"
            className="w-full bg-ink border-3 border-black pl-9 pr-3 py-2 font-mono text-xs text-cream focus:outline-none focus:border-neon"
            data-testid="admin-users-search" />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-40 bg-ink border-3 border-black text-cream font-mono text-xs" data-testid="admin-users-sort"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-ink2 border-3 border-black text-cream">
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="oldest">Oldest</SelectItem>
            <SelectItem value="active">Most Active</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="font-mono text-[11px] text-dim mb-2">{rows.length} of {users.length} users</p>
      <div className="comic-card overflow-x-auto" data-testid="admin-users-table">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b-3 border-black font-mono text-[10px] uppercase text-dim">
              <th className="p-3">User</th><th className="p-3 hidden sm:table-cell">Email</th>
              <th className="p-3">Role</th><th className="p-3 text-center">Posts</th>
              <th className="p-3 text-center hidden sm:table-cell">Memes</th>
              <th className="p-3 hidden md:table-cell">Joined</th><th className="p-3 hidden md:table-cell">Last Active</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? <tr><td colSpan={7} className="p-4 font-mono text-dim text-sm">Loading…</td></tr> :
              rows.map((u) => (
                <tr key={u.user_id} onClick={() => setDetailUserId(u.user_id)} data-testid={`admin-user-row-${u.user_id}`}
                  className="border-b-2 border-black/40 hover:bg-ink2 cursor-pointer transition-colors">
                  <td className="p-3">
                    <span className="font-display font-900 text-cream text-sm block">{u.displayName}</span>
                    {u.username && <span className="font-mono text-[10px] text-neon">@{u.username}</span>}
                  </td>
                  <td className="p-3 hidden sm:table-cell font-mono text-[11px] text-cream/80" data-testid={`admin-user-email-${u.user_id}`}>{u.email || <span className="text-dim">—</span>}</td>
                  <td className="p-3"><span className={`tag !text-[9px] !px-2 !py-0.5 ${u.role === "admin" ? "bg-grape text-cream" : "bg-ink3 text-cream"}`}>{u.role}</span></td>
                  <td className="p-3 text-center font-mono text-sm text-cream">{u.postsCount}</td>
                  <td className="p-3 text-center hidden sm:table-cell font-mono text-sm text-cream">{u.memesCount}</td>
                  <td className="p-3 hidden md:table-cell font-mono text-[11px] text-dim">{fmtDate(u.created_at)}</td>
                  <td className="p-3 hidden md:table-cell font-mono text-[11px] text-dim">{fmtDate(u.lastActivity)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <UserDetailDialog userId={detailUserId} onClose={() => setDetailUserId(null)} />
    </div>
  );
}

const FILTERS = [["all", "All"], ["published", "Published"], ["pending", "Pending"], ["removed", "Removed"]];
const FILTER_STATUS = { published: "approved", pending: "pending", removed: "rejected" };

export function AdminPostsPanel() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [detailUserId, setDetailUserId] = useState(null);
  const [viewPost, setViewPost] = useState(null);
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["admin-stories"],
    queryFn: async () => (await api.get("/admin/stories")).data,
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/admin/stories/${id}`),
    onSuccess: () => {
      toast.success("Post removed");
      qc.invalidateQueries({ queryKey: ["admin-stories"] });
      qc.invalidateQueries({ queryKey: ["stories"] });
      qc.invalidateQueries({ queryKey: ["admin-mod-count"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return posts.filter((p) => {
      if (filter !== "all" && p.status !== FILTER_STATUS[filter]) return false;
      if (!needle) return true;
      return (p.username || "").toLowerCase().includes(needle) || (p.email || "").toLowerCase().includes(needle) || (p.displayName || "").toLowerCase().includes(needle);
    });
  }, [posts, q, filter]);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`admin-posts-filter-${k}`}
            className={`px-3 py-1.5 font-mono text-[11px] uppercase border-2 transition-colors ${filter === k ? "border-neon text-neon bg-neon/10" : "border-charcoal text-dim hover:text-cream"}`}>{l}</button>
        ))}
      </div>
      <div className="relative mb-4">
        <Search className="w-4 h-4 text-dim absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by username or email…"
          className="w-full bg-ink border-3 border-black pl-9 pr-3 py-2 font-mono text-xs text-cream focus:outline-none focus:border-neon"
          data-testid="admin-posts-search" />
      </div>
      <p className="font-mono text-[11px] text-dim mb-2">{rows.length} of {posts.length} posts</p>
      <div className="space-y-3">
        {isLoading ? <p className="font-mono text-dim text-sm">Loading…</p> :
          rows.map((p) => (
            <div key={p.id} className="comic-card p-4 flex flex-col sm:flex-row gap-3" data-testid={`admin-post-row-${p.id}`}>
              {p.image_url
                ? <img src={IMG(p.image_url)} alt="meme" className="w-full sm:w-24 h-24 object-cover border-3 border-black bg-white shrink-0" />
                : <div className="w-full sm:w-24 h-24 grid place-items-center border-3 border-black bg-ink shrink-0 font-mono text-[9px] text-dim uppercase">Text</div>}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusTag s={p.status} />
                  <span className="font-display font-900 text-cream text-sm">{p.displayName || p.username || "Anon"}</span>
                  <span className="font-mono text-[10px] text-dim">@{p.username}</span>
                </div>
                <p className="font-mono text-[10px] text-cream/70 mt-0.5 flex items-center gap-1" data-testid={`admin-post-email-${p.id}`}>
                  <Mail className="w-3 h-3 text-dim" /> {p.email || <span className="text-dim">no email</span>}
                </p>
                {p.story && <p className="font-mono text-xs text-cream/90 mt-1 line-clamp-2">"{p.story}"</p>}
                <p className="font-mono text-[10px] text-dim mt-1">{fmtDate(p.created_at)} · ▲ {p.upvotes || 0}{p.isFavorite ? " · ★" : ""}</p>
              </div>
              <div className="flex sm:flex-col gap-2 shrink-0">
                <button onClick={() => setViewPost(p)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`admin-post-view-${p.id}`}><Eye className="w-3.5 h-3.5" /> View</button>
                {p.user_id && <button onClick={() => setDetailUserId(p.user_id)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`admin-post-openuser-${p.id}`}><UserIcon className="w-3.5 h-3.5" /> User</button>}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="border-3 border-black bg-ink3 text-danger p-1.5" data-testid={`admin-post-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-ink2 border-3 border-black text-cream">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-display font-900 uppercase text-danger">Remove this post?</AlertDialogTitle>
                      <AlertDialogDescription className="font-mono text-xs text-dim">This permanently deletes the post/meme from the Wall and can't be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="border-3 border-black bg-ink3 text-cream font-heading uppercase text-xs" data-testid={`admin-post-delete-cancel-${p.id}`}>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => del.mutate(p.id)} className="border-3 border-black bg-danger text-white font-heading uppercase text-xs" data-testid={`admin-post-delete-confirm-${p.id}`}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
      </div>

      <Dialog open={!!viewPost} onOpenChange={(o) => !o && setViewPost(null)}>
        <DialogContent className="bg-ink2 border-3 border-black text-cream max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-post-view-dialog">
          <DialogHeader><DialogTitle className="font-display font-900 uppercase text-neon">Post Detail</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Full post preview with creator, email and engagement.</DialogDescription>
          {viewPost && (
            <div className="space-y-3">
              <div className="flex items-center gap-2"><StatusTag s={viewPost.status} /><span className="font-mono text-[11px] text-dim">{fmtDate(viewPost.created_at)}</span></div>
              {viewPost.image_url && <img src={IMG(viewPost.image_url)} alt="meme" className="w-full border-3 border-black bg-white" />}
              {viewPost.story && <p className="font-mono text-sm text-cream/90">"{viewPost.story}"</p>}
              <div className="comic-card !bg-ink p-3 font-mono text-[11px] text-cream space-y-1">
                <p>{viewPost.displayName || "Anon"} · @{viewPost.username}</p>
                <p className="flex items-center gap-1"><Mail className="w-3 h-3 text-dim" /> {viewPost.email || "no email"}</p>
                <p className="text-dim">▲ {viewPost.upvotes || 0} upvotes · {viewPost.category}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <UserDetailDialog userId={detailUserId} onClose={() => setDetailUserId(null)} />
    </div>
  );
}
